import React, { useState, useEffect, useRef } from 'react';
import { withRouter, useLocation, useHistory } from 'react-router-dom';
import DataContext from './DataContext';
import { fetchConfig, fetchMarket, fetchPPIS, fetchPPSIS, fetchRefresh, saveInteractionsCount } from '../../api';
import { forOrgAndLang } from '../util/language';
import decoratePpsis from '../../features/ppsi/dataDecorator';
import decoratePpis from '../../features/ppi/dataDecorator';
import useRoutes from '../../routes/useRoutes';

function getOrgAndLangFromLocation(location = { pathname: '' }) {
  const segments = location.pathname.split('/');
  const org = segments.length > 3 ? segments[2].toUpperCase() : null;
  const lang = segments.length > 3 ? segments[3].toUpperCase() : null;
  return { org, lang };
}

const formatDateTime = (date) => {
  const pad = (n) => (n < 10 ? '0' + n : n);
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    ' ' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds())
  );
};

function DataProvider(props) {
  const history = useHistory();
  const [config, setConfig] = useState({});
  const [allPpsis, setAllPpsis] = useState([]);
  const [marketLanguage, setMarketLanguage] = useState(null);
  const [allPpis, setAllPpis] = useState([]);
  const [lang, setLang] = useState(null);
  const [org, setOrg] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const [isFetching, setIsFetching] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [allStores, setAllStores] = useState([]);
  const [allSalesPoint, setAllSalesPoint] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [interactionCount, setInteractionCount] = useState(0);
  const [query, setQuery] = useState('');
  const [showSuggestion, setShowSuggestion] = useState(false)
  const [suggestions, setSuggestions] = useState([]);
  const [marketList, setMarketList]=useState([]);

  const removeDish = allPpsis.filter(i => i?.salesPointCode?.length > 0 && i?.storeCode?.length > 0);

  let salesValues = JSON.parse(localStorage.getItem('salesArea')) || allSalesPoint;
  let storeValues = JSON.parse(localStorage.getItem('store')) || allStores;

  const sortedDish = removeDish.filter(i =>
    i.salesPointCode.some(v => salesValues.includes(v)) &&
    i.storeCode.some(j => storeValues.includes(j))
  );

  const ppsis = sortedDish;

  const allPpiDetail = (ppsis.flatMap(i => i.ppiDetails)).map(j => j.ppiEnrichmentDataId);
  const removeDuplicatePpi = [...new Set(allPpiDetail)];
  const ppis = allPpis;
  //.filter(i => removeDuplicatePpi.includes(i.id));
  // Fetch the market list from the API
  const getMarketList = async () => {
    try {
      const response = await fetchMarket();
      if (response.status === 200) {
        const sortedMarketList = response.data;
        setMarketList(sortedMarketList);
      }
    } catch (error) {
      console.error('Error fetching market list:', error.message);
    }
  };

  useEffect(() => {
    // Fetch market list once on mount
    getMarketList();
  }, []); // Only on initial render

  const isMarketAndLanguageAvailable = () => {
    const { org: countryCode, lang: languageCode } = getOrgAndLangFromLocation(location);  
    // Validate present in of languageCode and countryCode
    if (!countryCode || !languageCode) {
      history.push('/error'); // Redirect if either is missing
      return;
    }
  
    const baseLanguageCode = languageCode.split('-')[0].toUpperCase();
  
    const isValidMarket = marketList.some((market) => {
      if (market.countryCode === countryCode) {
        const languages = market.languageCode.split(',').map((lang) => lang.split('-')[0].toUpperCase());
        return languages.includes(baseLanguageCode);
      }
      return false;
    });
  
    if (isValidMarket) {
      fetchData(); // Fetch data if valid
    } else {
      setIsFetching(false);
      history.push('/error'); // Redirect to error page
    }
  };
  
  
  

  // Call fetchData or redirect based on the market and language availability
  useEffect(() => {
    if (marketList.length > 0) {
      isMarketAndLanguageAvailable();
    }
  }, [marketList]);
  const fetchData = () => {
    const { org: newOrg, lang: newLang } = getOrgAndLangFromLocation(location);
  
    if ((org !== newOrg || lang !== newLang) && !fetchError) {
      setIsFetching(true); // Indicate fetching state
      Promise.all([
        fetchPPSIS(newOrg),
        fetchPPIS(newOrg),
        fetchConfig(newOrg),
        fetchRefresh(newOrg),
      ])
        .then((response) => {
          const [ppsiResponse, ppisResponse, configResponse, countResponse] = response;
          const ppsis = decoratePpsis(ppsiResponse.data, configResponse.data.showAsNewDuration);
          const ppis = decoratePpis(ppisResponse.data, configResponse.data.showAsNewDuration);
  
          setConfig(configResponse.data);
          setAllPpsis(ppsis);
          setAllPpis(ppis);
          setRefreshCount(countResponse.data.counter);
          setLang(newLang);
          setOrg(newOrg);
          setAllStores(configResponse?.data?.storeCode?.map((i) => i.code));
          setAllSalesPoint(configResponse?.data?.salesPointCode?.map((i) => i.code));

          const rawSalesArea = localStorage.getItem('salesArea')
          const storageSalesArea = (rawSalesArea === null ) ? [] : JSON.parse(localStorage.getItem('salesArea'));
          const filteredSalesAreas = storageSalesArea
          .filter(itm=>configResponse.data.salesPointCode
          .filter(a=>a.isActive)
          .map(a=>a.code)
          .includes(itm) );
          if(storageSalesArea.length){
            localStorage.setItem('salesArea', JSON.stringify(filteredSalesAreas));
          }
        })
        .catch((error) => {
          console.error('Error in fetchData:', error);
          setFetchError(error);
        })
        .finally(() => {
          setIsFetching(false); // Reset fetching state
        });
    }
  };

  // useEffect(() => {
  //   fetchData();
  // }, [marketLanguage]);

  useEffect(() => {
    if (allStores.length > 0 && allSalesPoint.length > 0) {
      if (!localStorage.getItem('salesArea')) {
        localStorage.setItem('salesArea', JSON.stringify(allSalesPoint));
      }
      if (!localStorage.getItem('store')) {
        localStorage.setItem('store', JSON.stringify(allStores));
      }
    }
  }, [allStores, allSalesPoint]);

  const language = forOrgAndLang(org, lang);
  const location = useLocation();
//get browser details
function getBrowserDetails () {
  const userAgent = navigator.userAgent;
  let browserName = "Unknown";

  if (userAgent.includes("Edg/")) {
    browserName = "Edge";
  } else if (userAgent.includes("OPR/") || userAgent.includes("Opera")) {
    browserName = "Opera";
  } else if (userAgent.includes("Chrome") && !userAgent.includes("Chromium") && !userAgent.includes("Edg/")) {
    browserName = "Chrome";
  } else if (userAgent.includes("Firefox")) {
    browserName = "Firefox";
  } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
    browserName = "Safari";
  }

  return {
    browserName,
    userAgent,
    platform: navigator.platform,
    language: navigator.language,
  };
};
 // --------------------- INITIALIZE INTERACTION DATA ---------------------
const initializeInteractionData = () => {
  const { org: currentOrg } = getOrgAndLangFromLocation(location);
  const newInteractionData = {
    interactionCount: 0,
    date: formatDateTime(new Date()),
    countryCode: currentOrg,
    lastSentTime: null
  };
  setInteractionCount(0);
  localStorage.setItem("interactionData", JSON.stringify(newInteractionData));
};

// --------------------- LOAD INITIAL COUNT ---------------------
useEffect(() => {
  const interactionData = JSON.parse(localStorage.getItem("interactionData"));
  if (!interactionData) {
    initializeInteractionData();
  } else {
    setInteractionCount(interactionData.interactionCount || 0);
  }
}, []);

// --------------------- UPDATE COUNT ON LOCATION CHANGE ---------------------
useEffect(() => {
  const { org: newOrg } = getOrgAndLangFromLocation(location);
  const interactionData = JSON.parse(localStorage.getItem("interactionData"));

  if (interactionData && interactionData.countryCode === newOrg) {
    setInteractionCount((prev) => {
      const newCount = prev + 1;
      const updatedData = {
        ...interactionData,
        interactionCount: newCount,
        date: formatDateTime(new Date())
      };
      localStorage.setItem("interactionData", JSON.stringify(updatedData));
      return newCount;
    });
  } 
}, [location]);

// --------------------- SEND DATA FUNCTION ---------------------
const sendData = async (trigger, interactionDataOverride, opts = { skipLocalUpdate: false }) => {
  try {
    const interactionData =
      interactionDataOverride ||
      JSON.parse(localStorage.getItem("interactionData"));

    if (!interactionData) return;

    const browserDetails = getBrowserDetails();

    const payload = {
      coworker_interaction: {
        market: interactionData.countryCode,
        interactioncount: interactionData.interactionCount.toString(),
        interactiondate: formatDateTime(new Date(interactionData.date)),
        browserdetails: browserDetails.browserName
      }
    };

    const response = await saveInteractionsCount(payload);

    if (response.status === 200 && !opts.skipLocalUpdate) {
      const newData = {
        interactionCount: 0,
        date: formatDateTime(new Date()),
        countryCode: interactionData.countryCode,
        lastSentTime: trigger
      };
      localStorage.setItem("interactionData", JSON.stringify(newData));
      setInteractionCount(0);
    }
  } catch (error) {
    console.error("Error sending data:", error);
  }
};

// --------------------- FIXED TIMES SEND ---------------------
const FIXED_TIMES = ["12:00", "15:00", "18:00", "21:00"];
useEffect(() => {
  const intervalId = setInterval(() => {
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}`;

    const interactionData = JSON.parse(localStorage.getItem("interactionData"));
    if (!interactionData) return;

    if (FIXED_TIMES.includes(hhmm) && interactionData.lastSentTime !== hhmm) {
      sendData(hhmm);
    }
  }, 60000);

  return () => clearInterval(intervalId);
}, []);

// --------------------- MIDNIGHT SEND ---------------------
useEffect(() => {
  const intervalId = setInterval(() => {
    const now = new Date();
    if (now.getHours() === 23 && now.getMinutes() === 59) {
      sendData("23:59");
    }
  }, 60000);
  return () => clearInterval(intervalId);
}, []);

// --------------------- NEW DAY SEND ---------------------
useEffect(() => {
  const interactionData = JSON.parse(localStorage.getItem("interactionData"));
  if (!interactionData) return;

  const savedDate = formatDateTime(new Date(interactionData.date)).slice(0, 10);
  const currentDate = formatDateTime(new Date()).slice(0, 10);

  if (savedDate !== currentDate) {
    sendData("new-day");
  }
}, []);

// --------------------- MARKET CHANGE HANDLER (safe & correct) ---------------------
useEffect(() => {
  (async () => {
    // Get current market from URL
    const currentMarket = getOrgAndLangFromLocation({ pathname: window.location.pathname }).org;
    const previousMarket = localStorage.getItem("previousMarket");

    if (!previousMarket) {
      // First page load → just store current market
      localStorage.setItem("previousMarket", currentMarket);
      return;
    }

    if (previousMarket !== currentMarket) {
      console.log("MARKET CHANGED → sending PREVIOUS MARKET DATA");

      // 1️⃣ Immediately get old market data from localStorage
      const oldInteractionData = JSON.parse(localStorage.getItem("interactionData"));

      if (oldInteractionData) {
        // 2️⃣ Clone it to prevent any mutation
        const oldDataCopy = { ...oldInteractionData };

        // 3️⃣ Send previous market data using cloned object
        // Do NOT allow sendData to update localStorage
        try {
          await sendData("market-change", oldDataCopy, { skipLocalUpdate: true });
        } catch (err) {
          console.error("Failed to send previous market data:", err);
        }
      }

      // 4️⃣ Now safe to reset storage for new market
      localStorage.removeItem("interactionData");
      localStorage.removeItem("store");
      localStorage.removeItem("uniqueID");

      // 5️⃣ Initialize new market data
      generateUUID();
      const newInteractionData = {
        interactionCount: 1, // start new market with count = 1
        date: formatDateTime(new Date()),
        countryCode: currentMarket,
        lastSentTime: null
      };
      localStorage.setItem("interactionData", JSON.stringify(newInteractionData));
      setInteractionCount(1);

      // 6️⃣ Update previousMarket to current
      localStorage.setItem("previousMarket", currentMarket);
    } else {
      // No change → keep previousMarket updated
      localStorage.setItem("previousMarket", currentMarket);
    }
  })();
}, []);



//captcha uniqID
function generateUUID() { // Public Domain/MIT
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
  });
}

// Check if UUID exists, if not create a new one
let uniqueID = localStorage.getItem('uniqueID');
if (!uniqueID) {
  uniqueID = generateUUID();
  localStorage.setItem('uniqueID', uniqueID);
}
  const {
    overviewLabels: labels = [],
    timeUnits,
    temperatureUnits,
    firstStorage,
    production: productionOptions,
    utensils,
    ifoodPPSIUnitSystem,
    shelfLifeTimeUnits
  } = config;

  const allPpsisAndPpis = [
    ...ppsis.map(itm=>({...itm, type: 'Dish'})),
    ...ppis.map(itm=>({...itm, type: 'Ingredient'}))
  ]
  const handleQueryChange = (value) => {
    setQuery(value);
    setShowSuggestion(true)
    if (value.length > 0 && allPpsisAndPpis.length) {
      const filteredSuggestions = allPpsisAndPpis.filter(item =>
        item.info[language].presentationName.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filteredSuggestions);
    } else {
      if(allPpsisAndPpis.length){
        setShowSuggestion(false)
      }
      setSuggestions([]);
    }
  };

  const checkDataLength = () => {
    if(!ppsis.length && !ppis.length){
      setSuggestions([])
    }
  }

  const handleSuggestionClick = () => {
    setSuggestions([]);
    setQuery("")
  };

  return (
    <DataContext.Provider
      value={{
        ppsis,
        ppis,
        language,
        config,
        labels,
        timeUnits,
        temperatureUnits,
        firstStorage,
        productionOptions,
        utensils,
        isFetching,
        org,
        lang,
        unitSystem: ifoodPPSIUnitSystem,
        setMarketLanguage,
        allStores,
        allSalesPoint,
        refreshCount,
        setLang,
        modalOpen,
        setModalOpen,
        handleQueryChange,
        handleSuggestionClick,
        query,
        suggestions,
        setSuggestions,
        setQuery,
        showSuggestion, 
        setShowSuggestion,
        checkDataLength,
        shelfLifeTimeUnits,
        setInteractionCount
      }}
    >
      {props.children}
    </DataContext.Provider>
  );
}

export default withRouter(DataProvider);
